import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { ContactEditFormValues, ContactAddressRow } from "services/api/contacts/types";
import { getContactAddressesAPI, deleteContactAddressAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { ReactSVG } from "react-svg";
import { Loader } from "ui/components/Loader";
import { Dialog } from "ui/components/Dialog";
import ContactAddressEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactAddressEditCard/ContactAddressEditCard";
import { Badge } from "ui/components/Badge";

interface ContactAddressStepProps {
  formValues: ContactEditFormValues;
}

const ContactAddressStep: React.FC<ContactAddressStepProps> = ({ formValues: parentFormValues }) => {
  const contactId = parentFormValues.id;
  const { t } = useTranslate();
  const [addresses, setAddresses] = useState<ContactAddressRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editAddressId, setEditAddressId] = useState<string | null>(null);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  const fetchAddresses = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    try {
      const result = await getContactAddressesAPI(initialDataWithPagination.pagination, contactId);
      setAddresses(result.data);
    } catch {
      setAddresses([]);
    }
    setIsLoading(false);
  }, [contactId]);

  useEffect(() => {
    if (contactId) fetchAddresses();
  }, [contactId]);

  const handleEdit = (addressId: string) => {
    setEditAddressId(addressId);
    setShowEdit(true);
  };

  const handleAddNew = () => {
    setEditAddressId(null);
    setShowEdit(true);
  };

  const handleDelete = (addressId: string) => {
    setDeleteAddressId(addressId);
  };

  const handleDeleteConfirm = async () => {
    if (!contactId || !deleteAddressId) return;
    await deleteContactAddressAPI(t, contactId, deleteAddressId);
    setDeleteAddressId(null);
    fetchAddresses();
  };

  const handleEditSuccess = () => {
    setShowEdit(false);
    setEditAddressId(null);
    fetchAddresses();
  };

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Adrese</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Dodajte adrese kontakta klikom na gumb <strong>"Dodaj adresu"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          {isLoading ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <div className="px-3">
              {addresses.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <Button
                    variant="primary"
                    size="full"
                    startIcon="/media/icons/plus.svg"
                    children="Dodaj adresu"
                    onClick={handleAddNew}
                  />
                </div>
              ) : (
                <>
                  <div className="">
                    {addresses.map((address) => (
                      <div key={address.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0">
                        <div>
                          <div className="text-sm font-medium flex items-center gap-2"><span>{address.line1}</span>  {address.primary && <Badge text="Primarna" variant="info" height="small" />}</div>
                          {address.line2 && <div className="text-xs text-slate-500">{address.line2}</div>}
                          <div className="text-xs text-slate-500">{address.postalCode} {address.city}, {address.country}</div>
                         
                        </div>
                        <div className="flex gap-1">
                          <button type="button" onClick={() => handleEdit(address.id)} className="p-2 hover:bg-slate-200 rounded-full">
                            <ReactSVG src="/media/icons/edit.svg" className="w-4 h-4 stroke-slate-600" />
                          </button>
                          <button type="button" onClick={() => handleDelete(address.id)} className="p-2 hover:bg-red-100 rounded-full">
                            <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button variant="primary" startIcon="/media/icons/plus.svg" children="Dodaj adresu" onClick={handleAddNew} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactAddressEditCard
        contactId={contactId ?? ""}
        addressId={editAddressId ?? ""}
        show={showEdit}
        onSuccess={handleEditSuccess}
        onClose={() => setShowEdit(false)}
      />
      <Dialog
        open={!!deleteAddressId}
        title="Brisanje adrese"
        message="Jeste li sigurni da želite obrisati ovu adresu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteAddressId(null)}
      />
    </>
  );
};

export default memo(ContactAddressStep);
